
import {Sprite} from 'pixi.js';
import {IMenuBar, MenuBarButtonConfig} from '@gongxigames/framework/slot/modules/menubar/IMenuBar';
import {ButtonPanel, ButtonPanelConfig} from '@gongxigames/framework/slot/modules/menubar/ButtonPanel';
import {Button, CoreDI, FullScreenFader, LanguageService, ScreenParameters, Text, inject} from '@gongxigames/framework/core';
import {View} from '@gongxigames/framework/slot/fragments/module/view';
import { LAYERS } from '@gongxigames/framework/slot';

export class MenuBarView extends View implements IMenuBar {
    public static readonly NAME: symbol = Symbol('MenuBarView');

    public static readonly SOUND_BUTTON_CLICKED: symbol = Symbol('MenuBarView.SOUND_BUTTON_CLICKED');
    public static readonly MENU_BAR_OPENED: string = 'MENU_BAR_OPENED';
    public static readonly MENU_BAR_CLOSED: string = 'MENU_BAR_CLOSED';
    public static readonly PAYTABLE_CLICKED: string = 'PAYTABLE_CLICKED';
    public static readonly GAME_RULES_CLICKED: string = 'GAME_RULES_CLICKED';
    public static readonly HISTORY_CLICKED: string = 'HISTORY_CLICKED';

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    protected _menuButton!: Button;
    protected _menuBar!: Sprite;
    protected _label!: Text;
    protected _fader!: FullScreenFader;

    protected _buttonPanel?: ButtonPanel;
    protected _buttonPanelPosition = {
        width: 0,
        x: 0,
        y: 0
    };

    public init(): void {
        super.init();

        this._fader = this._displayObjectUtils.createFullScreenFader();
        this.container.addChild(this._fader);
        this.container.parentGroup = LAYERS.TOP.group;

        this.initMainSprite();
        this.initGraphicElements();

        this._menuButton.callback = this.onOpenMenuClicked.bind(this);

        this.container.addChild(this._menuBar);
        this.createButtonPanel();

        this.hide();

        this._label.text = '';
    }

    protected initMainSprite(): void {}

    protected initGraphicElements(): void {}

    protected createButtonPanel(): void {
        this._buttonPanel = new ButtonPanel(this.objectBuilder);
        this._menuBar.addChild(this._buttonPanel.container);
    }

    protected onOpenMenuClicked(): void {
        this.emit(MenuBarView.MENU_BAR_OPENED);
    }

    public updateButtonPanel(config: MenuBarButtonConfig): void {
        const buttonPanel = this._buttonPanel;

        if (!buttonPanel) {
            return;
        }

        this.setLabelText(config);

        const panelConfig = this.getButtonPanelConfig(config);

        this.updateButtonPanelPosition();

        buttonPanel.update(panelConfig);
        buttonPanel.container.position.copyFrom(this._buttonPanelPosition);
    }

    public onResized(screenParams: ScreenParameters): void {
        this._fader.resize(screenParams);
        this._menuBar.y = -(screenParams.y / screenParams.scale);
    }

    protected updateButtonPanelPosition(): void {}

    protected getButtonPanelConfig(config: MenuBarButtonConfig): ButtonPanelConfig {
        const panelConfig: ButtonPanelConfig = {
            width: this._buttonPanelPosition.width,
            buttons: []
        };

        panelConfig.buttons.push({
            ...config.paytable,
            icon: 'icon_info'
        });

        panelConfig.buttons.push({
            ...config.gameRules,
            icon: 'icon_game_rules'
        });

        if (!config.gameHistory.hide) {
            panelConfig.buttons.push({
                ...config.gameHistory,
                icon: 'icon_history'
            });
        }

        if (!config.soundOn.hide) {
            panelConfig.buttons.push({
                ...config.soundOn,
                icon: 'icon_sound'
            });
        }

        if (!config.soundOff.hide) {
            panelConfig.buttons.push({
                ...config.soundOff,
                icon: 'icon_sound_off'
            });
        }

        if (!config.cashier.hide) {
            panelConfig.buttons.push({
                ...config.cashier,
                icon: 'icon_cashier'
            });
        }

        if (!config.support.hide) {
            panelConfig.buttons.push({
                ...config.support,
                icon: 'icon_support'
            });
        }

        if (!config.logout.hide) {
            panelConfig.buttons.push({
                ...config.logout,
                icon: 'icon_logout'
            });
        }

        panelConfig.buttons.push({
            ...config.close,
            icon: 'icon_close'
        });

        return panelConfig;
    }

    private setLabelText(config: MenuBarButtonConfig): void {
        if (config.gameRules.active) {
            this._label.text = this._languageService.get('gameRules');
        }

        if (config.paytable.active) {
            this._label.text = this._languageService.get('paytable');
        }

        if (config.gameHistory.active) {
            this._label.text = this._languageService.get('gameHistory').toUpperCase();
        }
    }
}
